/**
 * Quote query key contract (issue #193).
 *
 * Central, explicit definition of every TanStack Query key the app uses for
 * client and admin quote data. Hooks and components MUST build their query
 * keys through these helpers so that future mutations can invalidate the
 * correct cached data without guessing the key shape.
 *
 * Hierarchy (TanStack Query treats a key as a prefix that matches every
 * descendant whose key starts with it):
 *
 *   client:  ['quotes']                              → all client quote data
 *             ['quotes', { status: 'pending' }]      → a filtered list
 *             ['quotes', '1']                        → a single client quote
 *
 *   admin:   ['admin', 'quotes']                     → all admin quote data
 *             ['admin', 'quotes', { status }]        → a filtered admin list
 *             ['admin', 'quotes', '1']               → a single admin quote
 *             ['admin', 'quotes', '1', 'contacted-companies']
 *                                                   → contacted companies for a quote
 *
 * Invalidating the namespace root refreshes every child key beneath it.
 */

export type QuoteListFilters = {
  status?: string;
  tracking_number?: string;
};

export const quoteKeys = {
  // Client-facing root used to invalidate every client quote key at once.
  all: ['quotes'] as const,

  lists(): readonly ['quotes'] {
    return this.all;
  },

  list(filters: QuoteListFilters = {}): readonly ['quotes'] | readonly ['quotes', QuoteListFilters] {
    const hasFilters = Object.keys(filters).length > 0;
    return hasFilters ? (['quotes', filters] as const) : this.all;
  },

  details(): readonly ['quotes'] {
    return this.all;
  },

  detail(id: string | undefined): readonly ['quotes', string | undefined] {
    return ['quotes', id] as const;
  },

  admin: {
    // Admin-facing root used to invalidate every admin quote key at once.
    all: ['admin', 'quotes'] as const,

    lists(): readonly ['admin', 'quotes'] {
      return this.all;
    },

    list(filters: QuoteListFilters = {}): readonly ['admin', 'quotes'] | readonly ['admin', 'quotes', QuoteListFilters] {
      const hasFilters = Object.keys(filters).length > 0;
      return hasFilters ? (['admin', 'quotes', filters] as const) : this.all;
    },

    details(): readonly ['admin', 'quotes'] {
      return this.all;
    },

    detail(id: string | undefined): readonly ['admin', 'quotes', string | undefined] {
      return ['admin', 'quotes', id] as const;
    },

    contactedCompanies(id: string | undefined): readonly ['admin', 'quotes', string | undefined, 'contacted-companies'] {
      return ['admin', 'quotes', id, 'contacted-companies'] as const;
    },
  },
};
